import { ThemeEvents } from '@theme/events';
import { scrollIntoView } from '@theme/scrolling';

/**
 * Delivery date + per-variant discount label for product-details-header (Living theme parity).
 */
function formatDeliveryDate(deliveryTimeInput, isWeeks) {
  const deliveryTime = String(deliveryTimeInput ?? '').trim();
  if (isWeeks) {
    if (deliveryTime.includes(',')) {
      const [minW, maxW] = deliveryTime.split(',').map((w) => parseInt(w.trim(), 10));
      if (minW === maxW) return `in ${minW} week${minW !== 1 ? 's' : ''}`;
      return `in ${minW}-${maxW} weeks`;
    }
    const weeks = parseInt(deliveryTime, 10);
    if (Number.isNaN(weeks)) return '';
    return `in ${weeks} week${weeks !== 1 ? 's' : ''}`;
  }

  const today = new Date();
  if (deliveryTime.includes(',')) {
    const [minDays, maxDays] = deliveryTime.split(',').map((d) => parseInt(d.trim(), 10));
    const minDate = new Date(today);
    const maxDate = new Date(today);
    minDate.setDate(minDate.getDate() + minDays);
    maxDate.setDate(maxDate.getDate() + maxDays);
    const minDay = minDate.getDate();
    const maxDay = maxDate.getDate();
    const minMonth = minDate.toLocaleDateString(undefined, { month: 'long' });
    const maxMonth = maxDate.toLocaleDateString(undefined, { month: 'long' });
    if (minDate.getMonth() === maxDate.getMonth()) {
      return `by ${minDay}-${maxDay} ${minMonth}`;
    }
    return `by ${minDay} ${minMonth} - ${maxDay} ${maxMonth}`;
  }
  const singleDate = new Date(today);
  singleDate.setDate(singleDate.getDate() + parseInt(deliveryTime, 10));
  if (Number.isNaN(singleDate.getTime())) return '';
  const formatted = singleDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  return `by ${formatted}`;
}

function updateDeliveryDate(root) {
  if (root.dataset.deliveryActive !== 'true') return;
  const nodes = root.querySelectorAll('[data-product-header-delivery-date]');
  if (!nodes.length) return;
  const formatted = formatDeliveryDate(root.dataset.deliveryDays, root.dataset.deliveryWeeks === 'true');
  if (!formatted) return;
  nodes.forEach((el) => {
    el.textContent = formatted;
  });
}

function updateDiscountFromVariant(root, variantId) {
  if (!variantId) return;
  const src = root.querySelector(`[variant-discount-display][variant-id="${variantId}"]`);
  const dest = root.querySelector('[data-product-header-discount]');
  if (src && dest) dest.textContent = src.textContent.trim();
}

/**
 * One flex row Lovable-parity: product price + PayItMonthly + Klarna.
 */
function clusterPriceAndFinance(root) {
  const price = root.querySelector(':scope > product-price');
  if (!price) return;
  if (price.closest('.mh-pdp-header-price-row')) return;

  const row = document.createElement('div');
  row.className = 'mh-pdp-header-price-row';
  price.before(row);
  row.appendChild(price);

  root.querySelectorAll(':scope > .product-details-header__finance--badge').forEach((el) => {
    row.appendChild(el);
  });
}

const SIZE_GUIDE_SPECS_ROW_HEADING = 'dimensions';

/**
 * Scrolls to the Specifications block and opens the row whose heading is "Dimensions",
 * used when the "Size guide display" setting is set to Specifications instead of Modal.
 * If that row doesn't exist (renamed or disabled), do nothing.
 */
function handleSizeGuideScroll(event) {
  event.preventDefault();
  const specsSection = document.querySelector('.mh-specs');
  if (!specsSection) return;

  const rowDetails = Array.from(specsSection.querySelectorAll('.mh-specs__list details[data-row-heading]')).find(
    (details) => details.dataset.rowHeading?.trim().toLowerCase() === SIZE_GUIDE_SPECS_ROW_HEADING
  );
  if (!rowDetails) return;

  rowDetails.open = true;
  scrollIntoView(rowDetails.closest('.mh-specs__row') ?? specsSection, { block: 'start' });
}

function bindSizeGuideScrollTriggers(root) {
  root.querySelectorAll('[data-size-guide-scroll-trigger]').forEach((btn) => {
    if (btn.dataset.sizeGuideBound === '1') return;
    btn.dataset.sizeGuideBound = '1';
    btn.addEventListener('click', handleSizeGuideScroll);
  });
}

function bind(root) {
  clusterPriceAndFinance(root);
  updateDeliveryDate(root);
  bindSizeGuideScrollTriggers(root);
  const section = root.closest('.shopify-section');
  if (!section || root.dataset.mhVariantBound === '1') return;
  root.dataset.mhVariantBound = '1';
  section.addEventListener(ThemeEvents.variantUpdate, (e) => {
    const id = e.detail?.resource?.id;
    if (id) updateDiscountFromVariant(root, String(id));
  });
}

function initRoot(root) {
  bind(root);
}

function init() {
  document.querySelectorAll('[data-product-header-root]').forEach(initRoot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  const scope = /** @type {HTMLElement} */ (event.target);
  if (!scope?.querySelectorAll) return;
  scope.querySelectorAll('[data-product-header-root]').forEach(initRoot);
});
